import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { supabase } from "@shared/integrations/supabase/client";
import { invokeFunction } from "@shared/functions.ts";
import GenerateDerivativeDatasetContract from "@shared/functions/generate-derivative-dataset.ts";
import {
  getAttackMethodsForDatasetKind,
  type AttackMethod,
} from "@/lib/adversaryAPI";
import { toast } from "sonner";

interface CreateDerivativeDialogProps {
  parentDataset: {
    id: string;
    name: string;
    dataset_kind: string;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: (datasetId: string, isManualCreation: boolean) => void;
  onCancel?: () => void;
}

export function CreateDerivativeDialog({
  parentDataset,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onSuccess,
  onCancel,
}: CreateDerivativeDialogProps) {
  // Internal state for uncontrolled mode
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange || setInternalOpen;

  const [derivativeName, setDerivativeName] = useState("");
  const [selectedAttackMethod, setSelectedAttackMethod] = useState<
    string | null
  >(null);
  const [attackMethods, setAttackMethods] = useState<AttackMethod[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // Load attack methods filtered by dataset kind
  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methods = await getAttackMethodsForDatasetKind(
          parentDataset.dataset_kind,
        );
        setAttackMethods(methods);
      } catch (error) {
        console.error("Failed to load attack methods:", error);
        toast.error("Failed to load attack methods. Manual creation only.");
      }
    };
    loadMethods();
  }, [parentDataset.dataset_kind]);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedAttackMethod(null);
      setDerivativeName("");
      onCancel?.();
    }
    onOpenChange(newOpen);
  };

  const handleCreateDerivative = async () => {
    if (!derivativeName.trim()) {
      toast.error("Adversarial dataset name is required");
      return;
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user?.id) {
      toast.error("You must be signed in to create an adversarial dataset");
      return;
    }

    // If attack method is selected, use the edge function to generate
    if (selectedAttackMethod) {
      setIsGenerating(true);
      try {
        const result = await invokeFunction(GenerateDerivativeDatasetContract, {
          client: supabase,
          body: {
            parentDatasetId: parentDataset.id,
            newDatasetName: derivativeName,
            attackMethod: selectedAttackMethod,
            userId: user.id,
          },
        });

        if (!result.ok) {
          throw new Error(result.error.message);
        }

        const payload = result.data;
        if (!("success" in payload)) {
          throw new Error(
            payload.error ?? "Failed to generate adversarial dataset",
          );
        }

        const selectedMethod = attackMethods.find(
          (m) => m.value === selectedAttackMethod,
        );
        toast.success(
          `Adversarial dataset generated with ${selectedMethod?.label || selectedAttackMethod}`,
        );
        setDerivativeName("");
        setSelectedAttackMethod(null);
        handleOpenChange(false);

        if (payload.datasetId) {
          onSuccess?.(payload.datasetId, false);
        }
      } catch (error) {
        console.error("Error generating adversarial dataset:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to generate adversarial dataset",
        );
      } finally {
        setIsGenerating(false);
      }
    } else {
      // Manual creation
      try {
        const { data: newDataset, error } = await supabase
          .from("test_data_sets")
          .insert([
            {
              name: derivativeName,
              type: "adversarial",
              parent_id: parentDataset.id,
              user_id: user.id,
              dataset_kind: (parentDataset.dataset_kind === "operational_rules"
                ? "behavior_examples"
                : parentDataset.dataset_kind) as
                | "behavior_examples"
                | "operational_rules",
              creation_method: "Manual",
            },
          ])
          .select()
          .single();

        if (error)
          throw new Error(
            error.message ?? "Failed to create adversarial dataset",
          );

        toast.success("Adversarial dataset created");
        setDerivativeName("");
        handleOpenChange(false);

        if (newDataset?.id) {
          onSuccess?.(newDataset.id, true); // Manual creation, should navigate
        }
      } catch (error) {
        console.error("Error creating adversarial dataset:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to create adversarial dataset",
        );
      }
    }
  };

  const selectedMethodLabel = selectedAttackMethod
    ? attackMethods.find((m) => m.value === selectedAttackMethod)?.label
    : null;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create Adversarial Dataset</DialogTitle>
					<DialogDescription>
						{selectedMethodLabel
							? `Generate an adversarial dataset using the ${selectedMethodLabel} attack method.`
							: "Create a new adversarial dataset for robustness testing."}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Creation Method</Label>
						<Select
							value={selectedAttackMethod ?? "manual"}
							onValueChange={(value) =>
								setSelectedAttackMethod(value === "manual" ? null : value)
							}
						>
							<SelectTrigger
								className="w-full"
								data-testid="creation-method-trigger"
							>
								<SelectValue placeholder="Select creation method" />
							</SelectTrigger>
							<SelectContent data-testid="creation-method-content">
								<SelectItem value="manual" data-testid="creation-method-manual">
									Manual Creation
								</SelectItem>
								{attackMethods.length > 0 && (
									<SelectGroup>
										<SelectLabel>Attack Methods</SelectLabel>
										{attackMethods.map((method) => (
											<SelectItem
												key={method.value}
												value={method.value}
												data-testid={`creation-method-${method.value}`}
											>
												{method.label}
											</SelectItem>
										))}
									</SelectGroup>
								)}
							</SelectContent>
						</Select>
					</div>

          {selectedAttackMethod && (
            <Alert>
              <AlertDescription>
                This adversarial dataset will be generated using the{" "}
                <Badge variant="secondary">{selectedMethodLabel}</Badge> attack
                type.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="derivative-name">Adversarial Dataset Name</Label>
            <Input
              id="derivative-name"
              value={derivativeName}
              onChange={(e) => setDerivativeName(e.target.value)}
              placeholder="Enter adversarial dataset name..."
              disabled={isGenerating}
            />
          </div>

          {isGenerating && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Generating adversarial dataset... This may take several minutes.
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleCreateDerivative}
            className="w-full"
            disabled={isGenerating || !derivativeName.trim()}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : selectedAttackMethod ? (
              "Generate Dataset"
            ) : (
              "Create Dataset"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
